import React, { useState } from "react";
import {
    Button, Col, Row, Form, Container,
} from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import LinearProgress from "@mui/material/LinearProgress";
import GRecaptcha from "app/features/GRecaptcha/GRecaptcha";
import { setPreferencesModalOpen } from "app/store/slices/authSlice";
import getBrandName from "app/shared/utils/BrandHelper";
import gRecaptchaManager from 'app/features/GRecaptcha/GRecaptchaManager';
import Constants from "app/shared/constants/constants";
import logging from "app/shared/utils/logging";
import { useUpdatePersonalInfoMutation } from "app/services/api/mutateGateAPI";
import NotificationBanner from "app/shared/components/NotificationBanner";
import { decrypt } from "app/shared/utils/EncryptionUtility";
import { RootState } from "../../store";
import { IPersonaInfoUpdateRequest } from "./types/SignUpForm";

const EnrollModal: React.FC = () => {
    const dispatch = useDispatch();

    const [updatePersonalInfo, { isLoading }] = useUpdatePersonalInfoMutation();

    const {
        allLogos,
        appSource,
        brandName,
        brandTitle,
        termsConditionURL,
    } = getBrandName();

    const appInsights = useAppInsightsContext();

    if (appInsights) {
        appInsights.trackPageView({
            name: "EnrollModal",
        });
        const metricData = {
            average: 5,
            name: "React Component Engaged Time (seconds)",
            sampleCount: 1,
        };
        const additionalProperties = { "Component Name": 'EnrollModal' };
        appInsights.trackMetric(metricData, additionalProperties);
    }

    const [isSpecialOfferChecked, setIsSpecialOfferChecked] = useState(true);
    const [isCateringChecked, setIsCateringChecked] = useState(true);
    const [isTermsChecked, setIsTermsChecked] = useState(false);

    const {
        preferencesModalOpen,
    } = useSelector((state: RootState) => state.auth);

    const { user } = useSelector((state: RootState) => state.auth);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const {
        firstName,
        lastName,
        email,
        phoneNumber,
        address,
        preferences,
    } = useSelector((state: RootState) => state.userInfo);

    const hideModal = () => {
        setErrorMessage(null);
        dispatch(setPreferencesModalOpen(false));
    };

    const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked, name } = event.currentTarget;

        if (name === 'isSpecialOfferChecked') {
            setIsSpecialOfferChecked(checked);
        } else if (name === 'isCateringChecked') {
            setIsCateringChecked(checked);
        } else if (name === 'isTermsChecked') {
            setIsTermsChecked(checked);
            setErrorMessage(null);
        }
    };

    const mapPreferences = () => {
        const preferencesPayload = {
            bruProgram: preferences.bruProgram,
            nnybCateringEmail: preferences.nnybCateringEmail,
            cateringEmail: preferences.cateringEmail,
            ebbProgram: preferences.ebbProgram,
            nnybProgram: preferences.nnybProgram,
            bruCateringEmail: preferences.bruCateringEmail,
            perksEmail: preferences.perksEmail,
            bruEmail: preferences.bruEmail,
            ebbEmail: preferences.ebbEmail,
            perksProgram: preferences.perksProgram,
            nnybEmail: preferences.nnybEmail,
        };

        if (brandName === Constants.EINSTEIN_APP) {
            preferencesPayload.ebbEmail = isSpecialOfferChecked;
            preferencesPayload.cateringEmail = isCateringChecked;
            preferencesPayload.ebbProgram = true;
        } else if (brandName === Constants.BRUEGGERS_APP) {
            preferencesPayload.bruEmail = isSpecialOfferChecked;
            preferencesPayload.bruCateringEmail = isCateringChecked;
            preferencesPayload.bruProgram = true;
        } else if (brandName === Constants.NOAH_APP) {
            preferencesPayload.nnybEmail = isSpecialOfferChecked;
            preferencesPayload.nnybCateringEmail = isCateringChecked;
            preferencesPayload.nnybProgram = true;
        }

        return preferencesPayload;
    };

    const onUpdatePersonalInfo = async () => {
        try {
            setErrorMessage(null);

            if (!isTermsChecked) {
                setErrorMessage("You must agree with our Terms and Conditions to continue!");
                return;
            }

            // eslint-disable-next-line max-len
            const recaptchaToken = await gRecaptchaManager.executeAndRetrieveToken(appInsights, "ENROLL") as string;

            const payload: IPersonaInfoUpdateRequest = {
                uid: user,
                sourceApp: appSource,
                recaptchaToken,
                updatePersonalInfo: {
                    personalInfo: {
                        firstName,
                        lastName,
                        memberPhone: phoneNumber,
                        emailAddress: email,
                        address: {
                            state: address.state,
                            zip: address.zip,
                            city: address.city,
                        },
                    },
                    preferences: mapPreferences(),
                },
            };

            logging.info(appInsights, 'evt_RewardLogin_req', "ENROLL_UPDATE_INFO", payload);
            const response = await updatePersonalInfo(payload).unwrap();

            const tempValue: string = decrypt(response.value.toString());
            // eslint-disable-next-line
            const data = JSON.parse(tempValue);

            // eslint-disable-next-line
            if (data.header?.status === 'Failure') {
                // eslint-disable-next-line
                const responseMsg: string = data?.header?.message || Constants.PERSONAL_INFO_UPDATE_ERROR_MSG;
                // eslint-disable-next-line
                setErrorMessage(responseMsg);
                return;
            }

            hideModal();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            // eslint-disable-next-line
            let message: string = error?.data?.message?.header?.message as string;
            // eslint-disable-next-line
            message = message || error?.data?.message as string || Constants.PERSONAL_INFO_UPDATE_ERROR_MSG;
            setErrorMessage(message);
        }
    };

    const onSubmit = () => {
        // eslint-disable-next-line
        onUpdatePersonalInfo();
    };

    const emailNotificationsLabel = (
        <p className="font-sub">Yes! I would like to receive special offers and promotions by email.</p>
    );

    const cateringEmailNotifications = (
        <p className="font-sub">Yes! I would like to receive catering offers and promotions.</p>
    );

    const termsConditions = (
        <p className="font-sub">
            I agree to the
            {" "}
            <a className="font-sub" href={termsConditionURL} target="_blank" rel="noreferrer">terms and conditions</a>
        </p>
    );

    const pageContent = (
        <div className="form-container">
            <Form noValidate>
                <Container className="d-flex flex-column gap-4">
                    <Row>
                        <Col>
                            <h2 className="font-main mb-2 text-center">Welcome Back!</h2>
                            {preferencesModalOpen && <GRecaptcha />}
                        </Col>
                    </Row>
                    <Row>
                        <Col className="d-flex flex-row justify-content-around">
                            <img
                                className="sign-up-logo  text-center"
                                style={{ height: '3rem' }}
                                src={allLogos?.ebbLogo}
                                alt="Sign up"
                            />

                            <img
                                className="sign-up-logo  text-center"
                                style={{ height: '3rem' }}
                                src={allLogos?.bruWelcomeBackLogo}
                                alt="Sign up"
                            />
                            <img
                                className="sign-up-logo  text-center"
                                style={{ height: '3rem' }}
                                src={allLogos?.nnybLogo}
                                alt="Sign up"
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <h5 className="text-center font-main">
                                You are already part of one of our rewards programs.
                                {" "}
                                Just a few more clicks to start earning rewards with
                                {" "}
                                <span className="text-capitalize">
                                    {`${brandTitle.toLowerCase()}.`}
                                </span>
                            </h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Check
                                id="isSpecialOfferChecked"
                                type="checkbox"
                                label={emailNotificationsLabel}
                                name="isSpecialOfferChecked"
                                checked={isSpecialOfferChecked}
                                onChange={handleCheckChange}
                                className="mb-3"
                            />
                            <Form.Check
                                id="isCateringChecked"
                                type="checkbox"
                                label={cateringEmailNotifications}
                                name="isCateringChecked"
                                checked={isCateringChecked}
                                onChange={handleCheckChange}
                                className="mb-3"
                            />
                            <Form.Check
                                id="isTermsChecked"
                                type="checkbox"
                                label={termsConditions}
                                name="isTermsChecked"
                                checked={isTermsChecked}
                                onChange={handleCheckChange}
                                className="mb-3"
                            />
                        </Col>
                    </Row>
                    {isLoading && (
                        <Row>
                            <Col className="d-flex justify-content-center">
                                <LinearProgress className="linear-progress" />
                            </Col>
                        </Row>
                    )}
                    {errorMessage && (
                        <Row>
                            <Col>
                                <NotificationBanner message={errorMessage} type="error" onClose={() => setErrorMessage(null)} />
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col className="d-flex justify-content-center">
                            <Button
                                className={`mt-2 mb-3 w-100 order-now-card-button ${isLoading ? 'disable-button' : ''}`}
                                variant="primary"
                                disabled={isLoading}
                                onClick={onSubmit}
                            >
                                Start Earning Rewards
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );

    return (
        <Modal
            style={{ zIndex: "9999" }}
            show={preferencesModalOpen}
            onHide={() => {
                hideModal();
            }}
            centered
            keyboard={false}
            backdrop="static"
        >
            <Modal.Body>
                <Container className="p-2">
                    {pageContent}
                </Container>
            </Modal.Body>
        </Modal>
    );
};

export default EnrollModal;
